import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'ffh:player-profile:recently-viewed';
const MAX_ENTRIES = 5;

export type RecentlyViewedPlayer = {
  /** Cross-season Opta identifier, matching the profile route's `id` query. */
  optaId: string;
  displayName: string;
  position: string;
  teamShortName: string;
  seasonPoints: number | null;
};

const readStorage = (): RecentlyViewedPlayer[] => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentlyViewedPlayer[]) : [];
  } catch {
    // A malformed or unreadable entry (private browsing, quota, hand-edited
    // value) is not worth surfacing — an empty history falls back to the
    // in-form cards, which is the same experience a first-time visitor gets.
    return [];
  }
};

export type UseRecentlyViewedPlayersResult = {
  recentlyViewed: RecentlyViewedPlayer[];
  recordView: (player: RecentlyViewedPlayer) => void;
};

/**
 * Recently viewed profiles, kept in local storage rather than Redux so the list
 * survives a reload and a new session.
 */
export const useRecentlyViewedPlayers = (): UseRecentlyViewedPlayersResult => {
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedPlayer[]>(
    []
  );

  // Read after mount rather than during the initial render so the server and
  // client markup match.
  useEffect(() => {
    setRecentlyViewed(readStorage());
  }, []);

  const recordView = useCallback((player: RecentlyViewedPlayer) => {
    setRecentlyViewed((current) => {
      const next = [
        player,
        ...current.filter((entry) => entry.optaId !== player.optaId)
      ].slice(0, MAX_ENTRIES);

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage being unavailable must not break the profile page; the list
        // simply stays in memory for this session.
      }

      return next;
    });
  }, []);

  return { recentlyViewed, recordView };
};
